import { useCallback, useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { RefreshCw, LogOut, Inbox } from "lucide-react";
import { API, authHeaders, clearToken, getToken } from "@/lib/api";

const STATUSES = ["new", "contacted", "booked", "completed"];

const statusStyles = {
  new: "border-acid text-acid",
  contacted: "border-zinc-400 text-zinc-300",
  booked: "border-olive text-olive",
  completed: "border-zinc-700 text-zinc-500",
};

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/quotes`, { headers: authHeaders() });
      setQuotes(data);
      setError("");
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        clearToken();
        navigate("/admin/login");
      } else {
        setError("Could not load quote requests. Try refreshing.");
      }
    }
  }, [navigate]);

  useEffect(() => {
    if (!getToken()) {
      navigate("/admin/login");
      return;
    }
    load();
  }, [load, navigate]);

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/quotes/${id}`, { status }, { headers: authHeaders() });
      setQuotes((qs) => qs.map((q) => (q.id === id ? { ...q, status } : q)));
    } catch {
      setError("Status update failed. Try again.");
    }
  };

  const sendReview = async (id) => {
    try {
      await axios.post(`${API}/admin/quotes/${id}/review-request`, {}, { headers: authHeaders() });
      setQuotes((qs) => qs.map((q) => (q.id === id ? { ...q, review_sent_at: new Date().toISOString() } : q)));
    } catch {
      setError("Review request email failed. Try again.");
    }
  };

  const logout = () => {
    clearToken();
    navigate("/admin/login");
  };

  const counts = (quotes || []).reduce(
    (acc, q) => ({ ...acc, [q.status]: (acc[q.status] || 0) + 1 }),
    {}
  );

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-ink text-zinc-100">
      <div className="noise-overlay" aria-hidden="true" />
      <header className="border-b border-seam">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 md:px-10">
          <Link to="/" data-testid="admin-logo" className="font-display text-2xl font-black italic uppercase tracking-tighter">
            Take The <span className="text-acid">Leap</span>
            <span className="ml-3 align-middle text-[10px] font-bold not-italic tracking-[0.3em] text-zinc-500">
              Quote Inbox
            </span>
          </Link>
          <div className="flex items-center gap-3">
            <Link
              data-testid="admin-gallery-link"
              to="/admin/gallery"
              className="border border-seam px-4 py-2.5 text-xs font-bold uppercase tracking-[0.2em] text-white transition-colors duration-200 hover:border-acid hover:text-acid"
            >
              Gallery
            </Link>
            <button
              data-testid="admin-refresh-button"
              onClick={load}
              className="flex items-center gap-2 border border-seam px-4 py-2.5 text-xs font-bold uppercase tracking-[0.2em] text-white transition-colors duration-200 hover:border-acid hover:text-acid"
            >
              <RefreshCw className="h-3.5 w-3.5" /> Refresh
            </button>
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="flex items-center gap-2 border border-seam px-4 py-2.5 text-xs font-bold uppercase tracking-[0.2em] text-zinc-400 transition-colors duration-200 hover:border-red-500 hover:text-red-400"
            >
              <LogOut className="h-3.5 w-3.5" /> Log out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 py-10 md:px-10">
        <div className="mb-10 grid grid-cols-2 gap-4 md:grid-cols-4">
          {STATUSES.map((s) => (
            <div key={s} data-testid={`admin-stat-${s}`} className="border border-seam bg-coal p-5">
              <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-zinc-500">{s}</p>
              <p className={`mt-2 font-display text-4xl font-black italic ${s === "new" ? "text-acid" : "text-white"}`}>
                {counts[s] || 0}
              </p>
            </div>
          ))}
        </div>

        {error && (
          <p data-testid="admin-error" className="mb-6 border border-red-900 bg-red-950/40 px-4 py-3 text-sm text-red-300">
            {error}
          </p>
        )}

        {quotes === null ? (
          <p className="py-20 text-center text-sm text-zinc-500">Loading quote requests…</p>
        ) : quotes.length === 0 ? (
          <div data-testid="admin-empty-state" className="flex flex-col items-center gap-4 border border-dashed border-seam py-24">
            <Inbox className="h-10 w-10 text-zinc-600" />
            <p className="text-sm text-zinc-500">No quote requests yet. They’ll land here the moment someone sends the form.</p>
          </div>
        ) : (
          <div className="overflow-x-auto border border-seam">
            <table data-testid="quotes-table" className="w-full min-w-[900px] text-left text-sm">
              <thead>
                <tr className="border-b border-seam bg-coal text-[10px] font-bold uppercase tracking-[0.25em] text-zinc-500">
                  <th className="px-5 py-4">Received</th>
                  <th className="px-5 py-4">Name</th>
                  <th className="px-5 py-4">Contact</th>
                  <th className="px-5 py-4">Service</th>
                  <th className="px-5 py-4">Details</th>
                  <th className="px-5 py-4">Status</th>
                  <th className="px-5 py-4">Review</th>
                </tr>
              </thead>
              <tbody>
                {quotes.map((q) => (
                  <tr key={q.id} data-testid={`quote-row-${q.id}`} className="border-b border-seam transition-colors duration-150 hover:bg-coal/60">
                    <td className="whitespace-nowrap px-5 py-4 text-zinc-500">{formatDate(q.created_at)}</td>
                    <td className="px-5 py-4 font-semibold text-white">{q.name}</td>
                    <td className="px-5 py-4">
                      <a href={`tel:${q.phone}`} className="block text-acid hover:underline">{q.phone}</a>
                      <a href={`mailto:${q.email}`} className="block text-zinc-400 hover:underline">{q.email}</a>
                    </td>
                    <td className="whitespace-nowrap px-5 py-4 text-zinc-300">{q.service_type}</td>
                    <td className="max-w-[260px] px-5 py-4 text-zinc-400">
                      <span className="block truncate" title={q.message}>{q.message || "—"}</span>
                      {q.photo_url && (
                        <a
                          data-testid={`quote-photo-${q.id}`}
                          href={`${process.env.REACT_APP_BACKEND_URL}${q.photo_url}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-2 inline-block text-xs font-bold uppercase tracking-[0.15em] text-acid hover:underline"
                        >
                          View photo
                        </a>
                      )}
                    </td>
                    <td className="px-5 py-4">
                      <select
                        data-testid={`quote-status-select-${q.id}`}
                        value={q.status}
                        onChange={(e) => updateStatus(q.id, e.target.value)}
                        className={`cursor-pointer rounded-none border bg-ink px-3 py-2 text-xs font-bold uppercase tracking-[0.15em] outline-none ${statusStyles[q.status] || statusStyles.new}`}
                      >
                        {STATUSES.map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-5 py-4">
                      {q.review_sent_at ? (
                        <span data-testid={`review-sent-${q.id}`} className="text-xs font-bold uppercase tracking-[0.15em] text-olive">
                          Sent
                        </span>
                      ) : q.status === "completed" ? (
                        <button
                          data-testid={`review-request-btn-${q.id}`}
                          onClick={() => sendReview(q.id)}
                          className="border border-seam px-3 py-2 text-xs font-bold uppercase tracking-[0.15em] text-white transition-colors duration-200 hover:border-acid hover:text-acid"
                        >
                          Send review link
                        </button>
                      ) : (
                        <span className="text-xs text-zinc-700">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}
